import { useState } from "react";
import { MessageSquare, Search, Zap, ArrowLeft, ArrowRight, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button-variants";
import { Link } from "react-router-dom";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import doctorAvatar1 from "@/assets/doctor-avatar-1.jpg";
import doctorAvatar2 from "@/assets/doctor-avatar-2.jpg";
import doctorAvatar3 from "@/assets/doctor-avatar-3.jpg";
import doctorAvatar4 from "@/assets/doctor-avatar-4.jpg";
import doctorAvatar5 from "@/assets/doctor-avatar-5.jpg";

const RecruitmentProcess = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState<any>(null);

  if (carouselApi) {
    carouselApi.on('select', () => {
      setCurrentSlide(carouselApi.selectedScrollSnap());
    });
  }

  const steps = [
    {
      icon: MessageSquare,
      title: "Parlez-nous de vos critères",
      description: "Échangez avec notre équipe pour définir vos besoins et préférences",
      gradient: "from-primary to-primary/80",
    },
    {
      icon: Search,
      title: "Recevez des missions pertinentes",
      description: "Nous sélectionnons pour vous les meilleures opportunités",
      gradient: "from-accent to-accent/80",
    },
    {
      icon: Zap,
      title: "Postulez en 1 clic — on gère la logistique",
      description: "Validez et c'est parti ! Logement, transport, admin : on s'occupe de tout",
      gradient: "from-secondary to-secondary/80",
    },
  ];


  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-white to-muted/30 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
      <div className="absolute top-20 left-1/3 w-96 h-96 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-1/3 w-96 h-96 bg-gradient-to-tl from-secondary/10 to-transparent rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/80 backdrop-blur-sm rounded-full mb-6 border border-primary/10 shadow-lg">
            <Stethoscope className="w-4 h-4 text-primary" />
            <span className="text-primary font-semibold text-sm tracking-wide">ESPACE MÉDECIN</span>
          </div>
          <h2 className="font-poppins font-black text-3xl md:text-5xl bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-4">
            3 étapes pour démarrer
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Un processus fluide et transparent, pensé pour vous
          </p>
        </div>

          {/* Unified card with steps and CTA */}
          <div className="relative group">
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-primary via-secondary to-accent rounded-3xl opacity-20 blur-2xl"></div>
            
            {/* Main card */}
            <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8 md:p-12">
              {/* Mobile Carousel */}
              <div className="md:hidden mb-8">
                <Carousel 
                  className="w-full max-w-lg mx-auto"
                  setApi={setCarouselApi}
                  opts={{
                    align: "center",
                    loop: true,
                  }}
                >
                  <CarouselContent>
                    {steps.map((step, index) => (
                      <CarouselItem key={index}>
                        <div className="p-1">
                          <div className="text-center">
                            <div className="relative inline-block mb-6">
                              <div className={`absolute -inset-2 bg-gradient-to-br ${step.gradient} rounded-3xl opacity-20 blur-2xl`}></div>
                              
                              <div className={`relative w-28 h-28 bg-gradient-to-br ${step.gradient} rounded-3xl flex items-center justify-center shadow-2xl`}>
                                <step.icon className="w-14 h-14 text-white" strokeWidth={2.5} />
                              </div>
                              
                              <div className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl border-4 border-primary/20">
                                <span className="font-black text-primary text-lg">{index + 1}</span>
                              </div>
                            </div>
                            
                            <div className="bg-gradient-to-br from-white/60 to-white/40 backdrop-blur-sm rounded-2xl p-6 border border-border/50">
                              <h3 className="font-poppins font-bold text-xl mb-3 text-foreground">
                                {step.title}
                              </h3>
                              <p className="text-muted-foreground text-sm leading-relaxed">
                                {step.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  
                  <CarouselPrevious className="left-2 top-[65%] -translate-y-1/2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20">
                    <ArrowLeft className="h-5 w-5" />
                  </CarouselPrevious>
                  <CarouselNext className="right-2 top-[65%] -translate-y-1/2 h-10 w-10 bg-white/90 backdrop-blur-sm text-primary hover:bg-primary hover:text-white shadow-lg border border-primary/20">
                    <ArrowRight className="h-5 w-5" />
                  </CarouselNext>
                </Carousel>
                
                <div className="flex justify-center gap-3 mt-6">
                  {steps.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => carouselApi?.scrollTo(index)}
                      className={`p-2 rounded-full transition-all duration-300 ${
                        currentSlide === index 
                          ? 'bg-primary' 
                          : 'bg-transparent'
                      }`}
                      aria-label={`Aller à la slide ${index + 1}`}
                    >
                      <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
                        currentSlide === index 
                          ? 'bg-white scale-125' 
                          : 'bg-muted-foreground/30'
                      }`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Desktop Grid */}
              <div className="hidden md:grid md:grid-cols-3 gap-8 md:gap-12 mb-12">
                {steps.map((step, index) => (
                  <div 
                    key={index} 
                    className="relative animate-fade-in"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    {/* Connection line for desktop */}
                    {index < steps.length - 1 && (
                      <div className="hidden md:block absolute top-16 left-[60%] w-full h-1 z-0">
                        <div className={`h-full bg-gradient-to-r ${step.gradient} opacity-30 rounded-full`}></div>
                        <div className="absolute top-1/2 -translate-y-1/2 right-0 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-8 border-l-primary/30"></div>
                      </div>
                    )}
                    
                    <div className="text-center relative z-10">
                      {/* Step icon with number */}
                      <div className="relative inline-block mb-6">
                        {/* Glow effect */}
                        <div className={`absolute -inset-2 bg-gradient-to-br ${step.gradient} rounded-3xl opacity-20 blur-2xl`}></div>
                        
                        {/* Icon container */}
                        <div className={`relative w-28 h-28 bg-gradient-to-br ${step.gradient} rounded-3xl flex items-center justify-center shadow-2xl hover:scale-105 transition-transform duration-300`}>
                          <step.icon className="w-14 h-14 text-white" strokeWidth={2.5} />
                        </div>
                        
                        {/* Step number badge */}
                        <div className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl border-4 border-primary/20">
                          <span className="font-black text-primary text-lg">{index + 1}</span>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-white/60 to-white/40 backdrop-blur-sm rounded-2xl p-6 border border-border/50">
                        <h3 className="font-poppins font-bold text-xl mb-3 text-foreground">
                          {step.title}
                        </h3>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="relative mb-10">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gradient-to-r from-transparent via-border to-transparent"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white/95 px-6 py-2 text-xs font-semibold text-muted-foreground rounded-full border border-border/50">
                    Prêt à commencer ?
                  </span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button 
                  variant="default" 
                  size="xl" 
                  className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-xl hover:shadow-2xl transition-all duration-500 hover:scale-105 group/btn"
                  asChild
                >
                  <Link to="/inscription-medecin" className="relative">
                    <span className="relative z-10 font-bold">Créer mon espace médecin</span>
                    <svg className="w-5 h-5 ml-2 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="xl" 
                  className="border-2 border-primary/30 bg-white/60 backdrop-blur-sm hover:bg-primary hover:text-white hover:border-primary shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-105"
                  asChild
                >
                  <Link to="/auth" className="font-semibold">
                    Se connecter
                  </Link>
                </Button>
              </div>

              {/* Trust badge */}
              <div className="text-center">
                <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-2 px-6 py-3 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full border border-primary/20">
                  <div className="flex -space-x-3">
                    {[doctorAvatar1, doctorAvatar2, doctorAvatar3, doctorAvatar4, doctorAvatar5].map((avatar, i) => (
                      <img 
                        key={i} 
                        src={avatar} 
                        alt=""
                        aria-hidden="true"
                        width="32" height="32" loading="lazy" decoding="async"
                        className="w-8 h-8 rounded-full border-2 border-white object-cover"
                      />
                    ))}
                  </div>
                  <p className="text-sm font-semibold text-foreground text-center sm:text-left">
                    Déjà <span className="text-primary font-bold">+500 médecins</span> nous font confiance
                  </p>
                </div>
              </div>
            </div>
          </div>
      </div>
    </section>
  );
};

export default RecruitmentProcess;